import {useMutation} from '@github-ui/react-query'
import {createRepositoryMilestoneMutation} from '@github-ui/server-persisted-queries'
import type {CreateRepositoryMilestoneMutationMutation} from '@github-ui/server-persisted-queries/types/createRepositoryMilestoneMutation'
import {reactFetchJSON} from '@github-ui/verified-fetch'
import {useCallback, useRef} from 'react'

import {LABELS} from './constants/labels'

export type CreateMilestoneFormInput = {
  title: string
  description?: string | null
  dueOn?: string | null
}

export type CreatedMilestone = NonNullable<
  NonNullable<CreateRepositoryMilestoneMutationMutation['createMilestone']>['milestone']
>

type UseCreateRepositoryMilestoneOptions = {
  repositoryId: string | null | undefined
  repositoryNameWithOwner: string | null | undefined
  setSubmissionErrors: (value: string | null) => void
  onSuccess: (milestone: CreatedMilestone) => void
}

// Creates a milestone through the `createRepositoryMilestoneMutation` server-persisted
// query and navigates to it on success (via `onSuccess`). There is no optimistic UI:
// the create page unmounts on success, and the mutation-lifecycle-events system only
// patches *existing* objects (`UPDATE_OBJECT_PROPERTIES_MUTATION_EVENT`), so it has no
// create/add event to emit. If a future change keeps the user on a list view after
// creating, emit lifecycle events / sync the list cache in that follow-up.
export function useCreateRepositoryMilestone({
  repositoryId,
  repositoryNameWithOwner,
  setSubmissionErrors,
  onSuccess,
}: UseCreateRepositoryMilestoneOptions) {
  const setIsSubmittingRef = useRef<(isSubmitting: boolean) => void>(() => {})

  const {mutate} = useMutation<CreateRepositoryMilestoneMutationMutation, Error, CreateMilestoneFormInput>({
    mutationFn: async (input: CreateMilestoneFormInput) => {
      // Normalize the nullable repository fields here rather than defaulting them at
      // the call site: without a repository we can't build a valid request, so surface
      // the generic create error instead of POSTing to a malformed URL.
      if (!repositoryId || !repositoryNameWithOwner) {
        throw new Error(LABELS.milestoneCreateError)
      }

      let response: Response
      try {
        response = await reactFetchJSON(`/${repositoryNameWithOwner}/milestones/new`, {
          method: 'POST',
          body: {
            queryId: createRepositoryMilestoneMutation,
            repositoryId,
            title: input.title,
            description: input.description,
            dueOn: input.dueOn,
          },
        })
      } catch {
        throw new Error(LABELS.milestoneCreateError)
      }

      if (!response.ok) {
        throw new Error(LABELS.milestoneCreateError)
      }

      const result: {data: CreateRepositoryMilestoneMutationMutation} = await response.json()

      // Mirror the Relay path's onCompleted handling exactly: a missing milestone or a
      // non-empty payload `errors` array both surface the generic create error, so
      // validation messaging does not change with the rollout flag.
      const errors = result.data.createMilestone?.errors
      if (!result.data.createMilestone?.milestone || (errors && errors.length > 0)) {
        throw new Error(LABELS.milestoneCreateError)
      }

      return result.data
    },
    onError: () => {
      // Every failure on this path — transport error, non-2xx, payload `errors`, a
      // missing milestone, or a malformed/empty success body that throws while parsing —
      // surfaces the generic create error, matching the Relay path (never a raw
      // parse/TypeError message).
      setSubmissionErrors(LABELS.milestoneCreateError)
      setIsSubmittingRef.current(false)
    },
    onSuccess: data => {
      // Reset the submitting state before handing off to the caller. The current
      // consumer navigates away on success (unmounting the form), but resetting here
      // keeps the hook correct for callers that stay mounted.
      setIsSubmittingRef.current(false)
      const created = data.createMilestone?.milestone
      if (created) {
        onSuccess(created)
      }
    },
  })

  return useCallback(
    (input: CreateMilestoneFormInput, setIsSubmitting: (isSubmitting: boolean) => void) => {
      setIsSubmittingRef.current = setIsSubmitting
      mutate(input)
    },
    [mutate],
  )
}
